import React, { useState } from 'react';
import { Container, Row, Col, Card, Modal, Button } from 'react-bootstrap';
import icons from './Icons';
import PlaceholderImage from './PlaceholderImage';
import './caseStudies.css';

const CaseStudies = ({ onNavigate }) => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const handleNavClick = (e, pageId) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate(pageId);
    }
  };

  const cases = [
    {
      id: 'd2c-fashion',
      client: 'D2C Fashion & Apparel',
      category: 'E-COMMERCE',
      theme: 'orange',
      title: 'Scaling D2C E-Commerce: 4.1x ROAS in 90 Days',
      shortSummary: 'Rebuilt the customer acquisition funnel, creative testing matrix, and conversion attribution for a high-growth apparel brand.',
      problem: 'An apparel retailer was struggling with rising customer acquisition costs and low ad returns (under 1.8x ROAS), compounded by iOS privacy changes and high drop-off on slow product pages.',
      strategy: 'We synchronized media buying and web performance: restructured Meta campaign funnels by purchase intent, launched weekly creative testing, and optimized mobile product pages for sub-1.5s load speeds.',
      execution: [
        'Audience segmentation: High-intent retargeting and lookalike testing',
        'Creative testing framework: Iterative video and carousel variations tested weekly',
        'Mobile page speed optimization: Cut page load time under 1.5s, reducing bounce rate by 55%',
        'Conversion API (CAPI) and Google Analytics 4 deep event mapping for accurate attribution'
      ],
      metrics: [
        { label: 'ROAS Delivered', value: '4.1x', color: 'orange' },
        { label: 'CAC Reduction', value: '44%', color: 'blue' },
        { label: 'Timeline', value: '90 Days', color: 'orange' },
        { label: 'Tested Variants', value: '15+', color: 'blue' }
      ]
    },
    {
      id: 'residential-property',
      client: 'Residential Property Group',
      category: 'REAL ESTATE / PROPTECH',
      theme: 'blue',
      title: 'Real Estate Lead Gen: 800+ Verified Leads in 60 Days',
      shortSummary: 'Hyper-localized Google and Meta campaigns paired with multi-step qualification funnels and automated WhatsApp CRM sales dispatch.',
      problem: 'A premium residential property developer needed genuine, qualified home buyers rather than casual inquiries. Previous campaigns resulted in low sales team contact rates and high lead drop-off.',
      strategy: 'Built an interactive qualification funnel with dynamic budget and location filters, directly connecting qualified inquiries to sales consultants via WhatsApp within 2 minutes.',
      execution: [
        'Geo-targeted Meta and Google Search campaigns focused on high-intent buyer keywords',
        'Multi-step mobile qualification form filtering by purchase budget and timeline',
        'CRM automation: instant lead distribution, automated WhatsApp booking and calendar sync',
        'Live sales dashboard tracking cost per lead, visit schedules, and agent follow-up times'
      ],
      metrics: [
        { label: 'Verified Inquiries', value: '800+', color: 'orange' },
        { label: 'CPL vs Industry', value: '-38%', color: 'blue' },
        { label: 'Visit Rate', value: '12%', color: 'orange' },
        { label: 'Timeline', value: '60 Days', color: 'blue' }
      ]
    },
    {
      id: 'b2b-saas-platform',
      client: 'B2B Analytics Platform',
      category: 'B2B SAAS',
      theme: 'purple',
      title: 'SaaS Platform Redesign: 2.1x Trial-to-Paid Conversion',
      shortSummary: 'Brand positioning overhaul, modern responsive UI redesign, and streamlined onboarding flow.',
      problem: 'A B2B analytics platform had powerful core software but lost enterprise leads due to a dated interface and a complex, friction-filled 6-step registration process.',
      strategy: 'Repositioned the value proposition around tangible customer outcomes, redesigned the web application with a modern component library, and reduced sign-up friction to 3 simple steps.',
      execution: [
        'Clear value-proposition messaging focused on customer ROI and team time savings',
        'Modern, responsive design system built for fast rendering and intuitive usability',
        'Simplified 3-step onboarding flow with interactive preview and guided walkthroughs',
        'Automated product activation emails triggered by user feature milestones'
      ],
      metrics: [
        { label: 'Trial → Paid', value: '2.1x', color: 'orange' },
        { label: 'Time to Value', value: '-52%', color: 'blue' },
        { label: 'Demo Bookings', value: '+89%', color: 'orange' },
        { label: 'Design System', value: '50+ comps', color: 'blue' }
      ]
    },
    {
      id: 'telehealth-network',
      client: 'Multi-Specialty Healthcare Network',
      category: 'HEALTHCARE / HEALTHTECH',
      theme: 'cyan',
      title: 'Automating Clinic Operations & Patient Scheduling',
      shortSummary: 'Custom patient portal, automated appointment workflows, and billing synchronization.',
      problem: 'A growing healthcare network was spending dozens of staff hours each week manually confirming doctor schedules, coordinating patient appointments, and updating clinic records.',
      strategy: 'Built an integrated operations portal combining self-service patient booking, automated SMS/WhatsApp reminders, and synchronized doctor calendar rosters.',
      execution: [
        'Custom patient appointment management portal with real-time doctor availability',
        'Automated WhatsApp and SMS confirmation workflows with instant reschedule options',
        'Secure billing integration and daily summary reports for clinic administrators',
        'Executive dashboard tracking daily patient volume, cancellations, and doctor utilization'
      ],
      metrics: [
        { label: 'Manual Work Cut', value: '75%', color: 'orange' },
        { label: 'No-Show Reduction', value: '-60%', color: 'blue' },
        { label: 'Weekly Hours Saved', value: '25 hrs', color: 'orange' },
        { label: 'Deployment', value: '6 Weeks', color: 'blue' }
      ]
    }
  ];

  const filteredCases = cases.filter(c => {
    if (activeFilter === 'all') return true;
    return c.theme === activeFilter || c.category.toLowerCase().includes(activeFilter.toLowerCase());
  });

  return (
    <div className="case-studies-page">
      {/* ── Hero Banner ── */}
      <section className="case-hero text-center">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} lg={10}>
              <span className="badge-eyebrow mb-3">SELECTED OUTCOMES & CASE STUDIES</span>
              <h1 className="case-hero-title">
                Measurable Work Across <br/> <span>Campaigns & Systems.</span>
              </h1>
              <p className="case-hero-subtitle">
                We don't deal in vanity metrics or empty deliverables. Every project is scoped around client context, strategic bottleneck, work deployed, and quantifiable financial return.
              </p>

              {/* Category Pills */}
              <div className="case-filter-tabs mt-4">
                <button
                  className={`case-tab-btn ${activeFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('all')}
                >
                  All Case Studies
                </button>
                <button
                  className={`case-tab-btn ${activeFilter === 'orange' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('orange')}
                >
                  E-Commerce & Retail
                </button>
                <button
                  className={`case-tab-btn ${activeFilter === 'blue' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('blue')}
                >
                  Real Estate & PropTech
                </button>
                <button
                  className={`case-tab-btn ${activeFilter === 'purple' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('purple')}
                >
                  B2B SaaS
                </button>
                <button
                  className={`case-tab-btn ${activeFilter === 'cyan' ? 'active' : ''}`}
                  onClick={() => setActiveFilter('cyan')}
                >
                  Healthcare & MedTech
                </button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ── Case Studies Feed ── */}
      <section className="case-feed py-5">
        <Container>
          <div className="d-flex flex-column gap-5">
            {filteredCases.map((cs) => (
              <Card key={cs.id} className="case-card-expanded p-0 overflow-hidden border-0">
                <Row className="g-0 align-items-stretch">
                  {/* Left Column: Visual Mockup / Placeholder */}
                  <Col xs={12} lg={5} className="case-visual-col d-flex flex-column justify-content-center p-4 p-md-5">
                    <PlaceholderImage
                      width={600}
                      height={420}
                      title={cs.client}
                      category={cs.category}
                      theme={cs.theme}
                    />
                  </Col>

                  {/* Right Column: Case Details & Metrics */}
                  <Col xs={12} lg={7} className="p-4 p-md-5 d-flex flex-column justify-content-between">
                    <div>
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <span className="case-client-name fw-bold">{cs.client}</span>
                        <span className="bullet-sep">•</span>
                        <span className="case-category-label">{cs.category}</span>
                      </div>

                      <h2 className="case-heading-h2 mb-3">{cs.title}</h2>
                      <p className="case-lead-p mb-4 text-secondary">{cs.shortSummary}</p>

                      <div className="case-content-blocks mb-4">
                        <div className="case-subblock mb-3">
                          <h6 className="case-subblock-title text-orange">THE CHALLENGE</h6>
                          <p className="case-subblock-text text-secondary">{cs.problem}</p>
                        </div>
                        <div className="case-subblock mb-3">
                          <h6 className="case-subblock-title text-blue">OUR STRATEGY</h6>
                          <p className="case-subblock-text text-secondary">{cs.strategy}</p>
                        </div>
                      </div>
                    </div>

                    {/* Metrics Grid */}
                    <div className="case-metrics-row p-3 rounded-3 mb-4">
                      <Row className="g-3 text-center">
                        {cs.metrics.map((m, mIdx) => (
                          <Col key={mIdx} xs={6} md={3}>
                            <div className="metric-item">
                              <span className={`metric-val ${m.color}`}>{m.value}</span>
                              <span className="metric-label d-block text-secondary">{m.label}</span>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>

                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <Button
                        variant="outline-secondary"
                        className="btn-case-expand"
                        onClick={() => setSelectedCase(cs)}
                      >
                        View Full Execution Details {icons.arrowRight}
                      </Button>
                      <a
                        href="#contact"
                        onClick={(e) => handleNavClick(e, 'contact')}
                        className="link-similar-results"
                      >
                        Achieve similar results
                      </a>
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Modal for In-depth Case Study View ── */}
      {selectedCase && (
        <Modal
          show={!!selectedCase}
          onHide={() => setSelectedCase(null)}
          size="lg"
          centered
          className="case-study-modal"
        >
          <Modal.Header closeButton className="border-secondary border-opacity-25">
            <div>
              <span className="badge-eyebrow mb-1">{selectedCase.category}</span>
              <Modal.Title className="fw-bold fs-4">{selectedCase.client}</Modal.Title>
            </div>
          </Modal.Header>
          <Modal.Body className="p-4">
            <h3 className="fs-5 fw-bold mb-3">{selectedCase.title}</h3>

            <div className="mb-4">
              <PlaceholderImage
                width={700}
                height={320}
                title={selectedCase.client}
                category="Deep Dive"
                theme={selectedCase.theme}
              />
            </div>

            <div className="modal-section mb-3">
              <h5 className="fw-bold text-orange">Problem Statement</h5>
              <p className="text-secondary">{selectedCase.problem}</p>
            </div>

            <div className="modal-section mb-3">
              <h5 className="fw-bold text-blue">Strategic Architecture</h5>
              <p className="text-secondary">{selectedCase.strategy}</p>
            </div>

            <div className="modal-section mb-4">
              <h5 className="fw-bold">Step-by-step Execution</h5>
              <ul className="modal-execution-list">
                {selectedCase.execution.map((step, idx) => (
                  <li key={idx} className="mb-2 text-secondary">
                    <span className="text-orange me-2">✔</span> {step}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-3 rounded-3 bg-secondary bg-opacity-10">
              <Row className="text-center">
                {selectedCase.metrics.map((m, idx) => (
                  <Col key={idx} xs={6} md={3}>
                    <strong className={`fs-4 d-block ${m.color === 'orange' ? 'text-warning' : 'text-primary'}`}>
                      {m.value}
                    </strong>
                    <small className="text-muted">{m.label}</small>
                  </Col>
                ))}
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-secondary border-opacity-25 justify-content-between">
            <Button variant="secondary" onClick={() => setSelectedCase(null)}>
              Close
            </Button>
            <Button
              href="#contact"
              onClick={(e) => {
                setSelectedCase(null);
                handleNavClick(e, 'contact');
              }}
              className="btn-start-project text-white"
            >
              Start Your Project With Us
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ── Ready to be next success story CTA ── */}
      <section className="case-cta py-5 text-center">
        <Container>
          <div className="p-5 rounded-4 cta-highlight-box">
            <h2 className="fw-bold mb-3">Ready to Be Our Next Success Story?</h2>
            <p className="text-secondary mx-auto mb-4" style={{ maxWidth: '640px' }}>
              Join 100+ growing brands that have scaled their revenue and automated operations with M Square Professionals.
            </p>
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
              className="btn-hero-primary d-inline-flex align-items-center gap-2"
            >
              <span>Book Your Strategy Call</span>
              {icons.arrowRight}
            </a>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default CaseStudies;
